/**
 * The sync pill and the queue drawer.
 *
 * WHY NOT `SyncStatusPill` from `@sonauto/ui`: the shared one models five
 * states (synced / syncing / pending / offline / error). The POS spec requires
 * six — it adds **Xung đột**, which is categorically different from an error:
 * an error retries itself, a conflict is stuck until a human decides. Showing
 * a conflict as "Lỗi đồng bộ" would tell a cashier to wait for something that
 * will never resolve on its own. So the pill is composed here from the shared
 * `Badge`, using the same tone vocabulary — it extends the design system rather
 * than forking it.
 */

import { useState } from 'react';
import { Badge, Button, Drawer, EmptyState } from '@sonauto/ui';
import type { Tone } from '@sonauto/ui';
import type { SyncState } from '@sonauto/offline-sync';
import { useOutbox } from './OutboxProvider';
import { ago, stamp } from '../lib/format';

const TONE: Record<SyncState, Tone> = {
  ONLINE: 'success',
  OFFLINE: 'danger',
  SYNCING: 'info',
  UNSYNCED: 'warning',
  ERROR: 'danger',
  CONFLICT: 'accent',
};

const LABEL: Record<SyncState, string> = {
  ONLINE: 'Trực tuyến',
  OFFLINE: 'Ngoại tuyến',
  SYNCING: 'Đang đồng bộ',
  UNSYNCED: 'Chưa đồng bộ',
  ERROR: 'Lỗi đồng bộ',
  CONFLICT: 'Xung đột',
};

export function SyncStatusPill(): React.JSX.Element {
  const outbox = useOutbox();
  const [open, setOpen] = useState(false);
  const { state, pendingCount, failedCount, conflictCount, lastSyncedAt } = outbox.snapshot;
  const queued = pendingCount + failedCount + conflictCount;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        title={lastSyncedAt === null ? LABEL[state] : `Đồng bộ gần nhất: ${stamp(lastSyncedAt)}`}
      >
        <Badge tone={TONE[state]} dot className={state === 'SYNCING' ? 'animate-pulse' : undefined}>
          {LABEL[state]}
          {queued > 0 ? ` · ${queued}` : ''}
        </Badge>
      </button>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="Hàng đợi ngoại tuyến"
        description="Các thao tác đã ghi nhận trên máy và đang chờ gửi lên máy chủ."
        side="right"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Đóng
            </Button>
            <Button onClick={() => outbox.flush()}>Gửi lại ngay</Button>
          </div>
        }
      >
        {outbox.records.length === 0 ? (
          <EmptyState
            title="Không có thao tác nào đang chờ"
            description="Mọi thao tác đã được gửi lên máy chủ."
          />
        ) : (
          <ul className="space-y-3">
            {outbox.records.map((record) => (
              <li key={record.id} className="rounded-md bg-surface-alt p-3 ring-1 ring-line">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-flow text-sm font-medium text-fg">{record.label}</p>
                    <p className="text-xs text-fg-subtle">
                      {record.kind} · {ago(record.createdAt)} · {record.attempts} lần thử
                    </p>
                  </div>
                  <Badge
                    tone={
                      record.status === 'CONFLICT'
                        ? 'accent'
                        : record.status === 'FAILED'
                          ? 'danger'
                          : record.status === 'SENDING'
                            ? 'info'
                            : 'warning'
                    }
                    size="sm"
                  >
                    {record.status}
                  </Badge>
                </div>
                {record.lastError !== null ? (
                  <p className="text-flow mt-2 text-xs text-danger-fg">{record.lastError}</p>
                ) : null}
                {record.status === 'CONFLICT' ? (
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => outbox.retryRecord(record.id)}>
                      Thử lại
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => outbox.discardRecord(record.id)}>
                      Bỏ qua
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Drawer>
    </>
  );
}
